import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify';

export const PurchaseOrdersManagementLoadingKeys = {
    getPurchaseOrders: "GET_PURCHASE_ORDERS",
    deletePurchaseOrder: "DELETE_PURCHASE_ORDER",
    createPurchaseOrder: "CREATE_PURCHASE_ORDER",
    getPurchaseOrderById: "GET_PURCHASE_ORDER_BY_ID",
    updatePurchaseOrder: "UPDATE_PURCHASE_ORDER"
}

const initialState = {
  purchaseOrdersManagement: [],
  purchaseOrderDetails: null,
  isFirstLoadComplete: false,
  hasLoaded: false,
  error: null,
  listRequestId: null,
  loadingKeys: [],
  pagination: {
    isTheLastPage: false,
    rangeRows: "",
    totalPages: 0
  },
  loading: false,
}

export const getPurchaseOrdersManagementRequest = createAsyncThunk(
  'purchaseOrders/getPurchaseOrdersManagementRequest',
  async ({
    search,
    page, 
    itemsPerPage,
    sortBy,
    sortDirection,
    filters
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/purchase-orders`, { params: { 
        search,
        page, 
        itemsPerPage,
        sortBy,
        sortDirection,
        filters
      }});
      return data;
    } catch (error) {
      const responseData = error?.response?.data;
      if ('string' === typeof responseData) {
        return rejectWithValue({ error: responseData })
      } else {
        return rejectWithValue({
          error: responseData?.errors || responseData?.error || error?.message || 'Unable to load purchase orders',
        })
      }
    }
  }
)

export const deletePurchaseOrder = createAsyncThunk(
  'purchaseOrders/deletePurchaseOrderManagement',
  async ({
    id,
    search,
    page, 
    itemsPerPage,
    sortBy,
    sortDirection,
    filters
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/soft-delete/purchase-orders/${id}`);
      dispatch(getPurchaseOrdersManagementRequest({ search, page, itemsPerPage, sortBy, sortDirection, filters }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const createPurchaseOrdersManagementRequest = createAsyncThunk(
  'purchaseOrders/createPurchaseOrdersManagementRequest',
  async ({
    poData
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/purchase-order`, { ...poData });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const createDraftPurchaseOrder = createAsyncThunk(
  'purchaseOrders/createDraftPurchaseOrder',
  async ({ poData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/purchase-order/draft`, poData);
      return data;
    } catch (error) {
      const responseData = error?.response?.data;
      return rejectWithValue({
        error: responseData?.error || error?.message || 'Unable to create draft',
        code: responseData?.code,
        lines: responseData?.lines,
      });
    }
  }
)

export const getPurchaseOrderById = createAsyncThunk(
  'purchaseOrders/getPurchaseOrderById',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/purchase-order/${id}`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const updatePurchaseOrder = createAsyncThunk(
  'purchaseOrders/updatePurchaseOrder',
  async ({ id, poData }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/purchase-order/${id}`, poData);
      // Refresh details so sidebars reflect updated quantities without manual reload
      try { await dispatch(getPurchaseOrderById({ id })); } catch (e) {}
      return data;
    } catch (error) {
      const resp = error?.response?.data;
      const message = (typeof resp === 'string')
        ? resp
        : (resp?.error || resp?.errors || error?.message || 'Unknown error');
      return rejectWithValue({ error: message, code: resp?.code, lines: resp?.lines })
    }
  }
)

export const purchaseOrdersManagementSlice = createSlice({
  name: 'purchaseOrdersManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseOrdersManagementRequest.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
        if (!state.loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.getPurchaseOrders)) {
          state.loadingKeys = [...state.loadingKeys, PurchaseOrdersManagementLoadingKeys.getPurchaseOrders];
        }
      })
      .addCase(getPurchaseOrdersManagementRequest.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.purchaseOrdersManagement = action.payload?.items || [];
        state.isFirstLoadComplete = true;
        state.hasLoaded = true;
        state.error = null;
        state.pagination.isTheLastPage = action.payload?.isTheLastPage;
        state.pagination.rangeRows = action.payload?.rangeRows;
        state.pagination.totalPages = action.payload?.totalPages;
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.getPurchaseOrders, state.loadingKeys);
      })
      .addCase(getPurchaseOrdersManagementRequest.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.isFirstLoadComplete = true;
        state.hasLoaded = true;
        state.error = action.payload?.error || action.error?.message || 'Unable to load purchase orders';
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.getPurchaseOrders, state.loadingKeys);
      })
      .addCase(deletePurchaseOrder.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, PurchaseOrdersManagementLoadingKeys.deletePurchaseOrder];
      })
      .addCase(deletePurchaseOrder.fulfilled, (state) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.deletePurchaseOrder, state.loadingKeys);
          toast.success(`Purchase order deleted successfully.`, { position: "bottom-right" })
      })
      .addCase(deletePurchaseOrder.rejected, (state, action) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.deletePurchaseOrder, state.loadingKeys);
          toast.error(
            `Purchase order can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(createPurchaseOrdersManagementRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, PurchaseOrdersManagementLoadingKeys.createPurchaseOrder];
      })
      .addCase(createPurchaseOrdersManagementRequest.fulfilled, (state) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.createPurchaseOrder, state.loadingKeys);
          toast.success(`Purchase order created successfully.`, { position: "bottom-right" })
      })
      .addCase(createPurchaseOrdersManagementRequest.rejected, (state, action) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.createPurchaseOrder, state.loadingKeys);
          toast.error(
            `Purchase order can't be created. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(createDraftPurchaseOrder.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, PurchaseOrdersManagementLoadingKeys.createPurchaseOrder];
      })
      .addCase(createDraftPurchaseOrder.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.createPurchaseOrder, state.loadingKeys);
        toast.success(`Purchase order draft created successfully.`, { position: "bottom-right" });
      })
      .addCase(createDraftPurchaseOrder.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.createPurchaseOrder, state.loadingKeys);
        if (action.payload?.code !== 'zero_cost_confirmation_required') {
          toast.error(`Purchase order draft can't be created. Error - ${action.payload?.error || action.error?.message}.`, { position: "bottom-right" });
        }
      })
      .addCase(getPurchaseOrderById.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          PurchaseOrdersManagementLoadingKeys.getPurchaseOrderById
        ];
      })
      .addCase(getPurchaseOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseOrderDetails = action.payload;
        state.loadingKeys = removeLoadingKey(
          PurchaseOrdersManagementLoadingKeys.getPurchaseOrderById,
          state.loadingKeys
        );
      })
      .addCase(getPurchaseOrderById.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(
          PurchaseOrdersManagementLoadingKeys.getPurchaseOrderById,
          state.loadingKeys
        );
        // toast.error(
        //   `Failed to load purchase order. ${action.error?.message}`,
        //   { position: "bottom-right" }
        // );
      })
      .addCase(updatePurchaseOrder.pending, (state) => {
        state.loading = true;
        state.loadingKeys.push(PurchaseOrdersManagementLoadingKeys.updatePurchaseOrder);
      })
      .addCase(updatePurchaseOrder.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.updatePurchaseOrder, state.loadingKeys);
        toast.success('Purchase order updated successfully.', { position: 'bottom-right' });
      })
      .addCase(updatePurchaseOrder.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(PurchaseOrdersManagementLoadingKeys.updatePurchaseOrder, state.loadingKeys);
        const msg = action.payload?.error || action.error?.message || 'Unknown error';
        if (action.payload?.code !== 'zero_cost_confirmation_required') {
          toast.error(`Failed to update purchase order. ${msg}`, { position: 'bottom-right' });
        }
      });
  }
})

export const {} = purchaseOrdersManagementSlice.actions;

export default purchaseOrdersManagementSlice.reducer
